/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 메시지 클래스를 정의한다.
 *
 * @file /src/components/message/Message.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 10. 3.
 */
import * as parent from '@base/Component';
import * as button from '@components/button/Button';

import { Window } from '@components/window/Window';
import { Progress } from '@components/progress/Progress';
import { Text } from '@components/text/Text';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Language, LanguageText } from '@utils/Language';
import { Exceptions } from '@utils/Exceptions';
import * as loading from '@components/loading/Loading';
import * as ajax from '@utils/Ajax';
import * as stream from '@utils/Stream';
import './Message.css';

export interface Common<T> extends parent.Properties<T> {
    /**
     * 제목 (텍스트 또는 Title 엘리먼트)
     */
    title?: LanguageText;

    /**
     * 메시지
     */
    message?: LanguageText;
}

export interface Properties<T> extends Common<T> {
    /**
     * 닫기허용 여부
     */
    closable?: boolean;

    /**
     * 메시지창 아이콘 DOM 요소
     */
    icon?: Dom;

    /**
     * 메시지박스 스타일시트 클래스
     */
    messageClass?: string;

    /**
     * 메시지창 buttons
     */
    buttons?: button.Properties<button.Button>[];

    /**
     * 메시지창 통합 핸들러
     */
    handler?: (button: button.Button, value: any) => Promise<void> | void;
}

export interface Info<T> extends Common<T> {
    /**
     * 메시지 핸들러
     */
    handler?: (button: button.Button) => Promise<void>;
}

export interface Confirm<T> extends Common<T> {
    /**
     * 예/아니오 버튼 여부 (기본값 false, false 인경우 취소/확인버튼)
     */
    isYesNo?: boolean;

    /**
     * 메시지 핸들러
     */
    handler?: (button: button.Button) => Promise<void>;
}

export interface Error<T> extends Common<T> {
    /**
     * 에러코드 (에러코드가 존재할 경우 message 보다 에러코드가 우선시 된다.)
     */
    code?: string;

    /**
     * 메시지 핸들러
     */
    handler?: () => Promise<void>;
}

export interface Delete<T> extends Common<T> {
    /**
     * 삭제를 처리할 URL
     */
    url?: string;

    /**
     * 삭제시 호출할 URL 매개변수
     */
    params?: ajax.Params;

    /**
     * 삭제시 호출할 데이터
     */
    data?: ajax.Data;

    /**
     * 삭제완료 메시지를 보일지 여부
     */
    completed?: boolean;

    /**
     * 삭제 핸들러
     */
    handler?: () => Promise<boolean>;
}

export interface Loading<T> extends Common<T> {
    /**
     * 로딩아이콘 타입
     */
    indicator?: loading.Properties<loading.Loading>['indicator'];

    /**
     * 로딩아이콘 및 로딩메시지의 위치방향
     */
    direction?: 'column' | 'row';
}

export interface Sync<T> extends Common<T> {
    /**
     * 프로그래스의 여러 단계가 존재할 경우
     */
    steps?: Sync<T>[];

    /**
     * 프로그래스바를 호출할 메소드
     */
    method?: 'GET' | 'POST' | 'SYNC' | 'EXCEL' | 'DELETE';

    /**
     * 프로그래스바를 호출할 URL
     */
    url?: string;

    /**
     * 프로그래스바를 호출할 URL 매개변수
     */
    params?: stream.Params;

    /**
     * 프로그래스바를 호출할 데이터
     */
    data?: stream.Data;

    /**
     * 요청이 진행중일 떄 프로그래스바를 조절하기 위한 핸들러
     */
    progress?: (progress: Progress, results: stream.Results) => void;

    /**
     * 요청완료 후 실행할 핸들러
     */
    handler?: (button: button.Button, results: stream.Results) => Promise<void>;
}

export interface ProgressMessage<T> extends Common<T> {
    /**
     * 취소버튼으로 진행중인 프로그래스를 닫을 수 있는지 여부 (기본값 : true)
     */
    closable?: boolean;

    /**
     * 메시지창이 열린 뒤 프로그래스바를 제어할 핸들러 (true 를 반환하면 메시지창을 자동으로 닫는다.)
     * signal 은 사용자가 메시지창을 닫으면 abort 된다.
     */
    progress?: (me: Window, progress: Progress, signal: AbortSignal) => Promise<boolean>;
}

export class Message {
    static message: Window = null;

    static INFO = Html.create('i').addClass('info');
    static ERROR = Html.create('i').addClass('error');
    static CONFIRM = Html.create('i').addClass('confirm');
    static LOADING = Html.create('i').addClass('loading').html('<i></i><i></i><i></i><i></i>');
    static OK = [{ text: Language.get('buttons.ok'), value: 'ok', buttonClass: 'confirm' }];
    static DANGER = [{ text: Language.get('buttons.ok'), value: 'ok', buttonClass: 'danger' }];
    static OKCANCEL = [
        { text: Language.get('buttons.cancel'), value: 'cancel' },
        { text: Language.get('buttons.ok'), value: 'ok', buttonClass: 'confirm' },
    ];
    static YESNO = [
        { text: Language.get('buttons.no'), value: 'cancel' },
        { text: Language.get('buttons.yes'), value: 'ok', buttonClass: 'confirm' },
    ];
    static DANGERCANCEL = [
        { text: Language.get('buttons.cancel'), value: 'cancel' },
        { text: Language.get('buttons.ok'), value: 'ok', buttonClass: 'danger' },
    ];

    /**
     * 메시지창을 연다.
     *
     * @param properties - 설정
     */
    static show(properties: Properties<Message> = null): void {
        Message.close();

        const handler =
            properties?.handler ??
            (() => {
                Message.close();
            });
        const buttons: button.Button[] = [];
        for (const p of (properties.buttons ?? []).map((b) => ({ ...b }))) {
            p.handler ??= async (button, value) => {
                await handler(button, value);
            };
            p.minWidth = 60;
            buttons.push(new button.Button(p));
        }

        const maxWidth = properties.width ?? 420;

        const text = new Text({
            minWidth: 300,
            maxWidth: maxWidth,
            listeners: {
                render: (me) => {
                    const $content = me.$getContainer();
                    if (properties?.messageClass) {
                        $content.addClass(...properties.messageClass.split(' '));
                    }

                    if (properties?.icon instanceof Dom) {
                        const $icon = Html.create('div', { 'data-role': 'icon' });
                        $icon.append(properties?.icon);
                        $content.append($icon);
                    }

                    const $message = Html.create('div', { 'data-role': 'message' });
                    const $text = Html.create('div', null, properties?.message ?? '');
                    $message.append($text);

                    $content.append($message);
                },
            },
        });

        Message.message = new Window({
            title: properties?.title ?? '',
            role: 'message',
            modal: true,
            scrollable: false,
            closable: properties.closable ?? false,
            items: properties?.items ?? [text],
            buttons: buttons,
            listeners: {
                close: () => {
                    Message.message = null;
                },
            },
        });

        Message.message.show();
    }

    /**
     * 안내 메시지창을 연다.
     *
     * @param properties - 설정
     */
    static info(properties: Omit<Info<Message>, 'title'> = null): void {
        Message.show({
            title: Language.get('texts.info'),
            icon: Message.INFO,
            message: properties.message ?? Language.getError('UNKNOWN'),
            closable: true,
            buttons: Message.OK,
            handler: async (button) => {
                if (typeof properties?.handler == 'function') {
                    await properties.handler(button);
                }
                Message.close();
            },
        });
    }

    /**
     * 선택확인 메시지창을 연다.
     *
     * @param properties - 설정
     */
    static confirm(properties: Omit<Confirm<Message>, 'title'> = null): void {
        Message.show({
            title: Language.get('buttons.ok'),
            icon: Message.CONFIRM,
            message: properties.message ?? Language.getError('UNKNOWN'),
            closable: false,
            buttons: properties.isYesNo === true ? Message.YESNO : Message.OKCANCEL,
            handler: async (button) => {
                if (typeof properties?.handler == 'function') {
                    await properties.handler(button);
                }
                Message.close();
            },
        });
    }

    /**
     * 오류 메시지창을 연다.
     *
     * @param properties - 설정
     */
    static error(properties: Omit<Error<Message>, 'title'> = null): void {
        Message.show({
            title: Language.get('texts.error'),
            icon: Message.ERROR,
            message: properties?.code
                ? Language.getError(properties.code)
                : (properties.message ?? Language.getError('UNKNOWN')),
            closable: true,
            buttons: Message.OK,
            handler: async () => {
                if (typeof properties?.handler == 'function') {
                    await properties.handler();
                }
                Message.close();
            },
        });
    }

    /**
     * 삭제 메시지창을 연다.
     *
     * @param properties - 설정
     */
    static delete(properties: Delete<Message> = null): void {
        Message.show({
            title: properties?.title ?? Language.get('buttons.ok'),
            icon: Message.CONFIRM.addClass('danger'),
            message: properties?.message ?? Language.getAction('DELETE_CONFIRM'),
            closable: true,
            buttons: Message.DANGERCANCEL,
            handler: async (button) => {
                if (button.value == 'ok') {
                    button.setLoading(true);

                    const url = properties?.url ?? null;
                    if (url !== null) {
                        try {
                            const results = await ajax.Ajax.delete(url, properties.params, properties.data, false);
                            if (results.success === true) {
                            }
                        } catch (e) {
                            Exceptions.print(Exceptions.base(e));
                            return;
                        }
                    }

                    if (properties.completed !== false) {
                        Message.info({
                            message: Language.getAction('DELETED'),
                            handler: async (button) => {
                                button.setLoading(true);

                                if (typeof properties?.handler == 'function') {
                                    const result = await properties.handler();
                                    if (result == true) {
                                        Message.close();
                                    } else {
                                        Message.error({
                                            code: 'FAILED_TO_DELETE',
                                        });
                                    }
                                }
                            },
                        });
                    } else if (typeof properties?.handler == 'function') {
                        const result = await properties.handler();
                        if (result == true) {
                            Message.close();
                        } else {
                            Message.error({
                                code: 'FAILED_TO_DELETE',
                            });
                        }
                    } else {
                        Message.close();
                    }
                } else {
                    Message.close();
                }
            },
        });
    }

    /**
     * 로딩 메시지창을 연다.
     *
     * @param properties - 설정
     */
    static loading(properties: Loading<Message> = null): void {
        Message.close();

        Message.message = new Window({
            title: properties?.title ?? Language.getAction('LOADING'),
            modal: true,
            movable: false,
            resizable: false,
            closable: false,
            width: 300,
            padding: 10,
            items: [
                new loading.Loading({
                    height: 60,
                    indicator: properties?.indicator ?? 'column',
                    direction: properties?.direction ?? 'column',
                    text: properties?.message ?? null,
                    background: false,
                }),
            ],
        });

        Message.message.show();
    }

    /**
     * 동기화 메시지창을 연다.
     *
     * @param properties - 설정
     */
    static sync(properties: Sync<Message>): void {
        Message.close();

        const steps = properties?.steps ?? [];

        const progressItem = new Progress({
            message: properties?.message ?? null,
            loading: true,
        });

        const stepProgressItem =
            steps.length > 0
                ? new Progress({
                      loading: true,
                      margin: [10, 0, 0, 0],
                  })
                : null;

        let currentStream: stream.Stream = null;
        let finalResults: stream.Results = null;

        const cancelButton = new button.Button({
            text: Language.get('buttons.cancel'),
            handler: () => {
                currentStream?.abort();
                Message.close();
            },
        });

        const okButton = new button.Button({
            text: Language.get('buttons.ok'),
            buttonClass: 'confirm',
            handler: async (btn) => {
                if (typeof properties?.handler == 'function') {
                    await properties.handler(btn, finalResults);
                }
                Message.close();
            },
        });

        const runStep = async (step: Sync<Message>): Promise<stream.Results> => {
            const method = (step?.method ?? 'SYNC').toUpperCase();
            const url = step?.url;
            const params = step?.params ?? {};
            const data = step?.data ?? {};

            progressItem.setMax(100);
            progressItem.setValue(0);

            await stream.Stream.sleep(1000);

            const callback = (streamData: stream.StreamData) => {
                if (Message.message === null) {
                    return;
                }

                currentStream = streamData.stream;

                progressItem.setMax(streamData.total);
                progressItem.setValue(streamData.current);

                if (typeof step?.progress == 'function' && streamData.success == true) {
                    step.progress(progressItem, streamData as stream.Results);
                }
            };

            switch (method) {
                case 'SYNC':
                    return stream.Stream.sync(url, params, callback);
                case 'POST':
                    return stream.Stream.post(url, data, params, callback);
                case 'EXCEL':
                    return stream.Stream.excel(url, data, params, callback);
                case 'DELETE':
                    return stream.Stream.delete(url, params, data, callback);
                default:
                    return stream.Stream.get(url, params, callback);
            }
        };

        const showError = async (results: stream.Results): Promise<void> => {
            Message.show({
                title: Language.get('texts.error'),
                icon: Message.ERROR,
                message: results?.message ?? Language.getError('FAILED_TO_LOAD_DATA'),
                closable: true,
                scrollable: false,
                buttons: Message.OK,
                handler: async (btn) => {
                    if (typeof properties?.handler == 'function') {
                        await properties.handler(btn, results);
                    }
                    Message.close();
                },
            });
        };

        Message.message = new Window({
            title: properties?.title ?? Language.getAction('SYNC'),
            modal: true,
            movable: false,
            resizable: false,
            closable: false,
            width: 400,
            padding: 10,
            items: stepProgressItem !== null ? [progressItem, stepProgressItem] : [progressItem],
            buttons: [cancelButton, okButton],
            listeners: {
                show: async () => {
                    okButton.setLoading(true);

                    if (steps.length > 0) {
                        const stepMessage =
                            (properties?.message ?? null) === null
                                ? '${current}/' + steps.length
                                : properties.message + ' (${current}/' + steps.length + ')';

                        stepProgressItem.setMax(steps.length);
                        stepProgressItem.setMessage(stepMessage.replace('${current}', '0'));

                        await stream.Stream.sleep(2000);

                        let allSuccess = true;
                        const stepResultList: stream.Results[] = [];

                        for (let index = 0; index < steps.length; index++) {
                            progressItem.setLoading(true);
                            progressItem.setMessage(steps[index]?.message ?? null);

                            await stream.Stream.sleep(1000);

                            stepProgressItem.setMessage(stepMessage.replace('${current}', index.toString()));
                            stepProgressItem.setValue(index);

                            const results = await runStep(steps[index]);
                            allSuccess = allSuccess && results.success == true;
                            stepResultList.push(results);

                            if (results.success == false) {
                                if (results.aborted !== true) {
                                    await showError(results);
                                }
                                break;
                            }

                            stepProgressItem.setMessage(stepMessage.replace('${current}', (index + 1).toString()));
                            stepProgressItem.setValue(index + 1);

                            await stream.Stream.sleep(2000);
                        }

                        if (allSuccess == true) {
                            finalResults = { success: true, steps: stepResultList } as stream.Results;
                            okButton.setLoading(false);
                            Message.message?.setTitle(Language.getAction('COMPLETED'));
                        }
                    } else {
                        await stream.Stream.sleep(2000);

                        const results = await runStep(properties);

                        if (results.success == true) {
                            finalResults = results;
                            okButton.setLoading(false);
                            Message.message?.setTitle(Language.getAction('COMPLETED'));
                        } else if (results.aborted !== true) {
                            await showError(results);
                        }
                    }
                },
            },
        });

        Message.message.show();
    }

    /**
     * 프로그래스 메시지창을 연다.
     *
     * @param properties - 설정
     */
    static progress(properties: ProgressMessage<Message> = null): void {
        Message.close();

        const controller = new AbortController();
        const closable = properties?.closable !== false;

        const progressWindow = new Window({
            title: properties?.title ?? Language.getAction('LOADING'),
            modal: true,
            movable: false,
            resizable: false,
            closable: false,
            width: 400,
            padding: 10,
            items: [
                new Progress({
                    message: properties?.message ?? null,
                    loading: true,
                }),
            ],
            buttons:
                closable == true
                    ? [
                          new button.Button({
                              text: Language.get('buttons.cancel'),
                              handler: () => {
                                  progressWindow.close();
                              },
                          }),
                      ]
                    : null,
            listeners: {
                show: async (me) => {
                    // progress 핸들러가 없으면 사용자가 직접 닫을 때까지 로딩상태를 유지한다.
                    if (typeof properties?.progress !== 'function') {
                        return;
                    }

                    let completed = false;
                    try {
                        completed = (await properties.progress(me, me.getItemAt(0), controller.signal)) === true;
                    } catch (e) {
                        if (controller.signal.aborted == false) {
                            Exceptions.print(Exceptions.base(e));
                        }
                    }

                    if (controller.signal.aborted == true) {
                        return;
                    }

                    if (completed == true) {
                        progressWindow.close();
                    }
                },
                close: () => {
                    controller.abort();
                },
            },
        });

        Message.message = progressWindow;
        Message.message.show();
    }

    /**
     * 열려있는 메시지창을 닫는다.
     */
    static close(): void {
        if (Message.message != null) {
            Message.message.close();
        }
    }
}
